import type { InputTargetConversation } from "@/api/conversation-targets";
import {
  WorkToastAppArt,
  WorkToastDenkerArt,
} from "@/components/toasts/work-toast-app-icons";
import { agentAvatarKind } from "@/lib/agent-avatar-kind";
import { workToastRecentApps } from "@/lib/work-toast-apps";
import { useProjectStore } from "@/stores/project-store";
import { useWorkToastStore } from "@/stores/work-toast-store";

/** The toast logo slot's round fill, so an inset integration logo has a circle. */
const ICON_CLASS =
  "grid h-5 w-5 shrink-0 place-items-center overflow-hidden rounded-full bg-[var(--color-surface-selected)]";

/** What the icon reads from a picker row; a routed target can supply the same fields. */
export type InputTargetRowIconSource = Pick<
  InputTargetConversation,
  "id" | "target_kind"
> & {
  project_id?: string;
  task?: { id: string } | null;
};

/** New chats, manual chats, agents and app tasks share one icon slot. The
 *  picker lists only Denker's chats and helpers, never a coding session, so a
 *  row shows the app its task operates or Denker's avatar (`agentAvatarKind`). */
export function InputTargetRowIcon({
  row,
  size = 20,
}: {
  row?: InputTargetRowIconSource;
  size?: number;
}) {
  const style = { width: size, height: size };
  const items = useWorkToastStore((state) => state.items);
  // A new chat has no row yet: it starts in the current project.
  const currentProjectId = useProjectStore((state) => state.currentProject?.id);
  const projectId = row ? row.project_id : currentProjectId;
  const task = Boolean(row?.task || row?.target_kind === "subagent");
  // Only a task row's work operates apps; a chat row is Denker itself.
  const app =
    row && task
      ? Object.values(items)
          .filter(
            (item) =>
              item.conversationId === row.id ||
              (row.task?.id && item.taskId === row.task.id),
          )
          .sort((a, b) => b.stateChangedAt - a.stateChangedAt)
          .flatMap((item) => workToastRecentApps(item))[0]
      : undefined;
  const avatar = agentAvatarKind({ hasApps: app !== undefined });
  if (avatar.kind === "app" && app) {
    return (
      <span
        className={ICON_CLASS}
        style={style}
        aria-hidden="true"
        data-testid="input-target-row-icon"
        data-icon-kind="app"
      >
        <WorkToastAppArt appRef={app} projectId={projectId} size={size} />
      </span>
    );
  }
  return (
    <span
      className={ICON_CLASS}
      style={style}
      aria-hidden="true"
      data-testid="input-target-row-icon"
      data-icon-kind="agent"
    >
      <WorkToastDenkerArt projectId={projectId} size={size} />
    </span>
  );
}
